/**
 * RequireMandatoryConsent — gate every authenticated route on the
 * presence of every mandatory DPDP consent.
 *
 * Public signup walks new users through the consent step before they
 * obtain a token. But three other paths bypass that flow:
 *
 *   * Admin / staff users created via team-invite (no public signup)
 *   * Existing users who signed up before the consent feature shipped
 *   * Any user when the platform bumps a mandatory policy version —
 *     their previous acceptance is for the old wording, so the gate
 *     re-prompts on the new version.
 *
 * For all of those, this gate intercepts the first authenticated render,
 * compares the user's recorded consents against the catalogue, and
 * redirects to ``/consent`` if any mandatory item is missing or
 * stale-version. The interstitial screen renders the catalogue and POSTs
 * grants before letting them through to ``/dashboard``.
 *
 * This sits *inside* ``ProtectedRoute`` (which already handles auth +
 * role gating). Drop it in App.jsx as a composition wrapper, not a
 * replacement.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import consentService from '../../services/consentService';

/** Returns true iff every mandatory catalogue entry has an active
 *  consent record at the same policy_version. */
const hasAllMandatoryConsents = (catalogue, records) => {
  const byCode = new Map();
  for (const r of records || []) {
    // Most-recent record wins (records are server-ordered desc).
    if (!byCode.has(r.consent_type)) byCode.set(r.consent_type, r);
  }
  return (catalogue || [])
    .filter((c) => c.mandatory)
    .every((c) => {
      const r = byCode.get(c.code);
      if (!r || !r.consent_given || r.revoked_at) return false;
      // Version drift triggers a re-prompt.
      if (r.policy_version && r.policy_version !== c.version) return false;
      return true;
    });
};

const RequireMandatoryConsent = ({ children }) => {
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const location = useLocation();
  const [state, setState] = useState({
    status: 'loading',
    needsConsent: false,
  });

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const [cat, mine] = await Promise.all([
          consentService.getCatalogue(),
          consentService.listMine(),
        ]);
        if (cancelled) return;
        const ok = hasAllMandatoryConsents(
          cat.data?.items ?? [],
          mine.data?.consents ?? [],
        );
        setState({ status: 'ready', needsConsent: !ok });
      } catch {
        // Fail-open — better to let the user reach the app than to
        // hard-block on a transient compliance-service blip. The gate
        // re-runs on the next protected route anyway.
        if (!cancelled) setState({ status: 'ready', needsConsent: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  // Don't run the gate on the consent screen itself — that would loop.
  if (location.pathname === '/consent') return children;

  if (state.status === 'loading') {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
        <Spinner /> <span className="ms-2 text-muted">Loading…</span>
      </div>
    );
  }

  if (state.needsConsent) {
    return <Navigate to="/consent" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireMandatoryConsent;
export { hasAllMandatoryConsents };
